import React, { useRef, useState, useCallback, forwardRef, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Tooltip from "../pages/addentities/Tooltip.js";
import { Info } from "lucide-react";

// Defined outside CalendarRangeInput so the component type is stable across renders.
// If defined inside, React sees a new type every render and remounts the input,
// which breaks datePickerRef.current.setOpen().
const SplitInput = forwardRef(
  ({ selectStart, selectEnd, activeField, isOpen, openForField, clearDate, formatLocalDate, borderColor }, ref) => (
    <div
      ref={ref}
      className="split-range-input"
      style={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${borderColor || "#212529"}`,
        borderRadius: "6px",
        padding: "0 10px",
        height: "40px",
        background: "#fff",
        gap: "6px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Start zone */}
      <div
        onClick={() => openForField("start")}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          fontSize: "13px",
          cursor: "pointer",
          overflow: "hidden",
          fontFamily: "Montserrat",
        }}
      >
        {isOpen && activeField === "start" && !selectStart ? (
          <span className="range-caret" />
        ) : (
          <span style={{ color: selectStart ? "#000" : "#6C757D", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {selectStart ? formatLocalDate(selectStart) : "Start Time"}
          </span>
        )}
      </div>

      <span style={{ color: "#aaa", flexShrink: 0 }}>–</span>

      {/* End zone */}
      <div
        onClick={() => openForField("end")}
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          fontSize: "13px",
          cursor: "pointer",
          overflow: "hidden",
          fontFamily: "Montserrat",
        }}
      >
        {isOpen && activeField === "end" && !selectEnd ? (
          <span className="range-caret" />
        ) : (
          <span style={{ color: selectEnd ? "#000" : "#6C757D", fontWeight: 400, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {selectEnd ? formatLocalDate(selectEnd) : "End Time"}
          </span>
        )}
      </div>

      {/* Clear */}
      {(selectStart || selectEnd) && (
        <span
          onClick={clearDate}
          style={{
            color: "#6C757D",
            cursor: "pointer",
            fontSize: "16px",
            lineHeight: 1,
            flexShrink: 0,
          }}
        >
          ×
        </span>
      )}

      {/* Calendar icon */}
      <span
        onClick={() => openForField(activeField)}
        style={{ flexShrink: 0, display: "flex", alignItems: "center", cursor: "pointer" }}
        dangerouslySetInnerHTML={{
          __html: `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="none">
  <path d="M6 1.5V4.5M12 1.5V4.5M2.25 7.5H15.75M3.75 3H14.25C15.078 3 15.75 3.672 15.75 4.5V15c0 .828-.672 1.5-1.5 1.5H3.75A1.5 1.5 0 0 1 2.25 15V4.5C2.25 3.672 2.922 3 3.75 3Z"
   stroke="#6C757D" stroke-width="2" stroke-linecap="round"/>
</svg>`,
        }}
      />
    </div>
  )
);

const CalendarFooter = ({ onApply, onCancel }) => (
  <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", padding: "10px 15px" }}>
    <button
      type="button"
      onClick={onCancel}
      style={{
        display: "flex",
        width: "60px",
        height: "24px",
        padding: "12px 18px",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "3px",
        border: "0.75px solid #000",
        background: "#FFF",
        cursor: "pointer",
        fontFamily: "Montserrat",
        fontSize: "10px",
        fontWeight: 600,
      }}
    >
      Cancel
    </button>
    <button
      type="button"
      onClick={onApply}
      style={{
        display: "flex",
        width: "60px",
        height: "24px",
        padding: "12px 18px",
        justifyContent: "center",
        alignItems: "center",
        borderRadius: "3px",
        background: "#000",
        color: "#FFF",
        border: "1px solid #000",
        cursor: "pointer",
        fontFamily: "Montserrat",
        fontSize: "10px",
        fontWeight: 600,
      }}
    >
      Apply
    </button>
  </div>
);

const CalendarRangeInput = ({
  label,
  name,
  startDate,
  endDate,
  onChange,
  onApplyRange,
  enableTime,
  placeholderText = "Select date range",
  wrapperClass = "",
  borderColor,
}) => {
  const datePickerRef = useRef(null);
  const [activeField, setActiveField] = useState("start");
  const [selectStart, setSelectStart] = useState(startDate);
  const [selectEnd, setSelectEnd] = useState(endDate);
  const [isOpen, setIsOpen] = useState(false);

  // Sync internal state when parent resets dates (e.g. on Clear).
  useEffect(() => {
    setSelectStart(startDate || null);
    setSelectEnd(endDate || null);
    if (!startDate && !endDate) setActiveField("start");
  }, [startDate, endDate]);

  const formatLocalDate = (date) => {
    if (!date) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    let val = `${yyyy}-${mm}-${dd}`;
    if (enableTime) {
      const hh = String(date.getHours()).padStart(2, "0");
      const min = String(date.getMinutes()).padStart(2, "0");
      val += ` ${hh}:${min}`;
    }
    return val;
  };

  const openForField = useCallback((field) => {
    setActiveField(field);
    setIsOpen(true);
    setTimeout(() => { if (datePickerRef.current) datePickerRef.current.setOpen(true); }, 0);
  }, []);

  const handleDateChange = (date) => {
    if (!date) return;

    if (activeField === "start") {
      if (selectEnd && date > selectEnd) {
        setSelectStart(selectEnd);
        setSelectEnd(date);
        setActiveField("end");
        onChange({ target: { name, value: { start: selectEnd, end: date } } });
      } else {
        setSelectStart(date);
        onChange({ target: { name, value: { start: date, end: selectEnd } } });
      }
    } else {
      if (selectStart && date < selectStart) {
        setSelectEnd(selectStart);
        setSelectStart(date);
        setActiveField("start");
        onChange({ target: { name, value: { start: date, end: selectStart } } });
      } else {
        setSelectEnd(date);
        onChange({ target: { name, value: { start: selectStart, end: date } } });
      }
    }
  };

  const clearDate = useCallback((e) => {
    if (e) e.stopPropagation();
    setSelectStart(null);
    setSelectEnd(null);
    onChange({ target: { name, value: { start: null, end: null } } });
  }, [name, onChange]);

  const applyRange = () => {
    if (activeField === "start" && selectStart && !selectEnd) {
      setActiveField("end");
      setIsOpen(true);
      setTimeout(() => { if (datePickerRef.current) datePickerRef.current.setOpen(true); }, 0);
      return;
    }

    if (activeField === "end" && selectEnd && !selectStart) {
      setActiveField("start");
      setIsOpen(true);
      setTimeout(() => { if (datePickerRef.current) datePickerRef.current.setOpen(true); }, 0);
      return;
    }

    let finalStart = selectStart;
    let finalEnd = selectEnd;
    if (finalStart && finalEnd && finalStart > finalEnd) {
      finalStart = selectEnd;
      finalEnd = selectStart;
      setSelectStart(finalStart);
      setSelectEnd(finalEnd);
      onChange({ target: { name, value: { start: finalStart, end: finalEnd } } });
    }

    onApplyRange && onApplyRange();
    setIsOpen(false);
    if (datePickerRef.current) datePickerRef.current.setOpen(false);
  };

  const cancelRange = () => {
    setSelectStart(null);
    setSelectEnd(null);
    onChange({ target: { name, value: { start: null, end: null } } });
    setIsOpen(false);
    if (datePickerRef.current) datePickerRef.current.setOpen(false);
  };

  useEffect(() => {
    if (!isOpen || !enableTime) return;

    let observer;

    const sync = () => {
      const monthContainer = document.querySelector(".custom-calendar .react-datepicker__month-container");
      const timeContainer = document.querySelector(".custom-calendar .react-datepicker__time-container");
      if (!monthContainer || !timeContainer) return;

      const monthHeight = monthContainer.offsetHeight;
      timeContainer.style.height = monthHeight + "px";
      timeContainer.style.overflowY = "hidden";

      const timeHeader = timeContainer.querySelector(".react-datepicker-time__header");
      const timeList = timeContainer.querySelector(".react-datepicker__time-list");
      if (timeList) {
        const headerH = timeHeader ? timeHeader.offsetHeight : 0;
        const listH = monthHeight - headerH;
        timeList.style.height = listH + "px";
        timeList.style.maxHeight = listH + "px";
        timeList.style.overflowY = "auto";
      }
    };

    const t = setTimeout(() => {
      sync();
      const monthContainer = document.querySelector(".custom-calendar .react-datepicker__month-container");
      if (monthContainer) {
        observer = new ResizeObserver(sync);
        observer.observe(monthContainer);
      }
    }, 0);

    return () => {
      clearTimeout(t);
      if (observer) observer.disconnect();
    };
  }, [isOpen, enableTime]);

  const savedScrollTop = useRef(null);

  const handleTimeMouseDown = (e) => {
    const list = e.currentTarget.querySelector(".react-datepicker__time-list");
    if (list) savedScrollTop.current = list.scrollTop;
  };

  const handleTimeClick = (e) => {
    if (savedScrollTop.current === null) return;
    const list = e.currentTarget.querySelector(".react-datepicker__time-list");
    if (list) {
      const saved = savedScrollTop.current;
      // restore after datepicker's own scroll runs
      requestAnimationFrame(() => { list.scrollTop = saved; });
    }
    savedScrollTop.current = null;
  };

  // useCallback so the container component reference is stable
  const CalendarContainer = useCallback(
    ({ className, children }) => (
      <div className={className} style={{ display: "flex", flexDirection: "column" }}>
        <div
          // style={{ flex: "1 1 auto" }}
          onMouseDownCapture={handleTimeMouseDown}
          onClickCapture={handleTimeClick}
        >
          {children}
        </div>
        <CalendarFooter onApply={applyRange} onCancel={cancelRange} />
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeField, selectStart, selectEnd]
  );

  const calendarClass =
    selectStart && selectEnd ? "custom-calendar range-selected" : "custom-calendar";

  return (
    <div className={`info-inner${wrapperClass ? ` ${wrapperClass}` : ""}`}>
      {label && (
        <label className="form-label d-flex align-items-center gap-1">
          {label}
          {name === "release_date" && (
            <Tooltip
              title="Released Date"
              body="The time when the SPU is released to the market"
            >
              <Info size={16} className="info-icon" />
            </Tooltip>
          )}
        </label>
      )}

      <DatePicker
        ref={datePickerRef}
        selected={activeField === "start" ? selectStart : selectEnd}
        onChange={handleDateChange}
        shouldCloseOnSelect={false}
        dateFormat={enableTime ? "yyyy-MM-dd HH:mm" : "yyyy-MM-dd"}
        id={name}
        popperClassName={calendarClass}
        customInput={
          <SplitInput
            selectStart={selectStart}
            selectEnd={selectEnd}
            activeField={activeField}
            isOpen={isOpen}
            openForField={openForField}
            clearDate={clearDate}
            formatLocalDate={formatLocalDate}
            borderColor={borderColor}
          />
        }
        openToDate={
          activeField === "start"
            ? selectStart || new Date()
            : selectEnd || selectStart || new Date()
        }
        showMonthDropdown
        showYearDropdown
        dropdownMode="select"
        calendarContainer={CalendarContainer}
        onKeyDown={(e) => e.preventDefault()}
        startDate={selectStart}
        endDate={selectEnd}
        selectsStart={activeField === "start"}
        selectsEnd={activeField === "end"}
        {...(enableTime && {
          showTimeSelect: true,
          timeFormat: "HH:mm",
          timeIntervals: 15,
          timeCaption: "Time",
        })}
      />

      <style>{`
        .react-datepicker-wrapper,
        .react-datepicker__input-container {
          width: 100%;
        }

        @keyframes range-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }

        .range-caret {
          display: inline-block;
          width: 1.5px;
          height: 13px;
          background: #000;
          margin-left: 1px;
          flex-shrink: 0;
          animation: range-blink 1s step-start infinite;
        }

        .custom-calendar .react-datepicker {
          border-radius: 18px !important;
          padding: 18px;
          border: none;
          box-shadow: 0px 2px 15px rgba(0,0,0,0.15);
          overflow: hidden !important;
        }

        .custom-calendar .react-datepicker__header {
          background: transparent;
          border: none !important;
          font-size: 16px;
          margin-top: -20px;
        }

        .custom-calendar .react-datepicker__current-month {
          display: none !important;
        }

        .custom-calendar select {
          border: none;
          background: transparent;
          border-radius: 6px;
          padding: 3px 6px;
          font-size: 14px;
          margin-top: -10px;
        }

        .custom-calendar .react-datepicker__day {
          width: 32px !important;
          height: 32px !important;
          line-height: 32px !important;
          display: inline-flex !important;
          align-items: center;
          justify-content: center;
        }

        .custom-calendar .react-datepicker__day--keyboard-selected {
          background: transparent !important;
          color: inherit !important;
        }

        .custom-calendar .react-datepicker__day:hover {
          background-color: #FFEB00 !important;
          border-radius: 50% !important;
        }

        .custom-calendar .react-datepicker__day--in-selecting-range {
          background-color: transparent !important;
          color: inherit !important;
        }

        .custom-calendar .react-datepicker__day--range-start,
        .custom-calendar .react-datepicker__day--selected {
          background-color: #FFEB00 !important;
          color: #000 !important;
          border-radius: 50% !important;
          border: none !important;
        }

        .custom-calendar .react-datepicker__day--in-range {
          background-color: transparent !important;
          color: inherit !important;
        }

        .custom-calendar .react-datepicker__day--range-end {
          background-color: #FFEB00 !important;
          color: #000 !important;
          border-radius: 50% !important;
        }

        .custom-calendar .react-datepicker__day--outside-month {
          background: transparent !important;
          color: #ccc !important;
          border: none !important;
        }

        .custom-calendar:not(.range-selected)
          .react-datepicker__day--today {
            background: #FFEB00 !important;
            border-radius: 50% !important;
            font-weight: 600;
        }

        .custom-calendar .react-datepicker__navigation {
          display: none !important;
        }

        .custom-calendar .react-datepicker {
          font-family: 'Montserrat', sans-serif !important;
        }

        // .custom-calendar .react-datepicker__month-container {
        //   min-height: 360.95px;
        // }

        .custom-calendar .react-datepicker__month {
          flex: 1;
        }

        .custom-calendar .react-datepicker__time-container,
        .custom-calendar .react-datepicker__time-list-item,
        .custom-calendar .react-datepicker__time-box,
        .custom-calendar .react-datepicker-time__header {
          font-family: 'Montserrat', sans-serif !important;
        }

        .custom-calendar .react-datepicker-time__header {
          padding-top: 12px !important;
        }

        .custom-calendar .react-datepicker__time-container,
        .custom-calendar .react-datepicker__time-box,
        .custom-calendar .react-datepicker__time-list {
          overflow-x: hidden !important;
        }

        .custom-calendar .react-datepicker__time-list-item--selected {
          background-color: #FFEB00 !important;
          color: #000 !important;
          font-weight: 600 !important;
        }

        .custom-calendar .react-datepicker__time-list-item:hover {
          background-color: #FFEB00 !important;
          color: #000 !important;
        }
      `}</style>
    </div>
  );
};

export default CalendarRangeInput;
